import React, { useEffect } from 'react';
// Optimized intersection observer utility with subtle animations
export function useIntersectionObserver(selector: string, callback: (entry: IntersectionObserverEntry) => void, options: IntersectionObserverInit = {
  threshold: 0.1,
  rootMargin: '0px 0px 100px 0px'
}, dependencies: any[] = []) {
  useEffect(() => {
    // Find elements once at the beginning
    const elements = document.querySelectorAll(selector);
    if (elements.length === 0) return;
    // Create a single observer for all elements
    const observer = new IntersectionObserver(entries => {
      entries.forEach(callback);
    }, options);
    // Start observing all elements
    elements.forEach(el => observer.observe(el));
    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, dependencies);
}
// Subtle reveal animation hook that won't impact page speed
export function useRevealAnimation(selector: string = '.reveal-on-scroll', dependencies: any[] = []) {
  useEffect(() => {
    // Initial setup - hide elements with opacity 0
    document.querySelectorAll(selector).forEach(el => {
      if (!el.classList.contains('revealed')) {
        // Set initial state with inline styles to avoid FOUC
        el.style.opacity = '0';
        el.style.transform = 'translateY(10px)';
        el.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      }
    });
    // Create observer with appropriate options
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Only animate when in viewport
          const element = entry.target as HTMLElement;
          // Small delay for staggered effect
          setTimeout(() => {
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
            element.classList.add('revealed');
          }, Math.random() * 150); // Slight random delay for natural feel
          // Stop observing after animation
          observer.unobserve(element);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    // Start observing all elements with the selector
    document.querySelectorAll(selector).forEach(el => {
      if (!el.classList.contains('revealed')) {
        observer.observe(el);
      }
    });
    return () => {
      observer.disconnect();
    };
  }, dependencies);
}